/* Cart "Swap for better value" — assets/cart-swap.js
   Loaded by sections/cart-drawer.liquid (drawer-scoped, not global).

   A line item whose product has a `custom.swap_product` (product_reference)
   metafield shows a swap pill. Clicking it replaces the line with the swap
   product's default variant; an "undo" then appears on the swapped line to
   revert. Swap state is kept in sessionStorage keyed by the swap variant id so
   the undo survives reloads and each line is independent.

   Only ONE swap pill is visible at a time: Liquid renders a pill on every
   eligible line, and restoreState() shows just the first eligible one (in DOM
   order — cart.items is newest-first, so the last-added product wins), so
   swapping/undoing one line reveals the next line's offer. Undoing a swap
   dismisses that line's offer (sessionStorage) so the pill doesn't come back —
   until the product itself is removed from the cart, which forgets the
   dismissal so re-adding it starts fresh.

   Reuses the theme's own cart events (theme:cart:change + theme:cart:reload) to
   re-render the drawer and refresh totals. */
   
(function () {
  var STORE_KEY = 'asaya:cart-swaps';
  var DISMISS_KEY = 'asaya:cart-swap-dismissed';
  var ITEMS = '[data-line-items]';
  /* Count of cart operations (swap/undo) currently hitting the network. Used only to
     debounce a new swap while one is in flight; undo is always allowed so it can
     supersede an in-flight swap — that's what makes undo feel instant. */
  var inflight = 0;
  /* In-memory clones of the original line's DOM, keyed by swap variant id, captured
     at swap time. Used to restore the original line instantly on undo (no network on
     the render path). Not persisted — if it's gone (e.g. after a reload) undo falls
     back to the loading-indicator path below. */
  var swapDom = {};
  /* In-flight swap commits, keyed by swap variant id. Lets an undo clicked before the
     swap's network settles sequence its reversal behind the commit (so the two writes
     don't race) and tell the commit not to re-render, so the undo's own reload is the
     single reconcile. */
  var pending = {};

  function getStore() {
    try {
      return JSON.parse(sessionStorage.getItem(STORE_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function setStore(store) {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify(store));
    } catch (e) {
      /* sessionStorage unavailable — swap still works, undo just won't persist */
    }
  }

  // Variant ids whose swap offer was undone — those lines never show the pill again.
  function getDismissed() {
    try {
      return JSON.parse(sessionStorage.getItem(DISMISS_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function setDismissed(dismissed) {
    try {
      sessionStorage.setItem(DISMISS_KEY, JSON.stringify(dismissed));
    } catch (e) {
      /* sessionStorage unavailable — dismissal just won't persist across renders */
    }
  }

  function addDismissed(variantId) {
    if (!variantId) return;
    var dismissed = getDismissed();
    dismissed[variantId] = true;
    setDismissed(dismissed);
  }

  function cartBase() {
    return (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) {
        return res.json().catch(function () { return {}; }).then(function (data) {
          throw new Error(data.description || data.message || res.statusText);
        });
      }
      // A success body that isn't JSON (e.g. challenge page) shouldn't crash the
      // flow — the follow-up theme:cart:reload refetches the real state anyway.
      return res.json().catch(function () { return null; });
    });
  }

  // Cart API error bodies carry shopper-friendly text (e.g. stockout); network and
  // parse failures don't — show a generic message for those instead of raw noise.
  function friendlyError(err) {
    var msg = err && err.message;
    if (!msg || /unexpected token|failed to fetch|load failed|networkerror/i.test(msg)) {
      return 'Could not update cart, please try again';
    }
    return msg;
  }

  function showError(message) {
    var el = document.querySelector('[data-drawer="drawer-cart"] [data-form-errors]');
    if (!el) return;
    el.innerHTML = '<p>' + message + '</p>';
    el.style.display = 'block';
    clearTimeout(showError.timer);
    showError.timer = setTimeout(function () {
      el.style.display = 'none';
    }, 5000);
  }

  // Show the line's horizontal loadbar while the swap/undo request is in flight —
  // same indicator the quantity and variant controls use.
  function showLoadbar(el) {
    var row = el && el.closest('[data-cart-item]');
    var loadbar = row && row.querySelector('[data-item-loadbar]');
    if (loadbar) loadbar.style.display = 'block';
    return loadbar;
  }

  function copyText(line, fromSel, toSel) {
    var from = line.querySelector(fromSel);
    var to = line.querySelector(toSel);
    if (to) to.textContent = from ? from.textContent.trim() : '';
  }

  /* Optimistic swap: repaint this line with the suggested product's preview
     (image/title/price are already rendered inside the swap pill, and the line
     shows per-unit price so they match at any quantity), then hide the pill. The
     change shows instantly; reload() reconciles the authoritative line. */
  function applyOptimisticSwap(line) {
    if (!line) return;
    var previewImg = line.querySelector('.cart-swap__preview-img');
    var lineImg = line.querySelector('.cart-line__img');
    if (previewImg && lineImg) {
      if (previewImg.currentSrc || previewImg.src) lineImg.src = previewImg.currentSrc || previewImg.src;
      lineImg.srcset = previewImg.srcset || '';
    }
    copyText(line, '.cart-swap__preview-title', '.cart-line__title');
    copyText(line, '.cart-swap__preview-current', '.cart-line__price-current');
    copyText(line, '.cart-swap__preview-was', '.cart-line__price-was');
    // Drop the original line's discount badge — it belongs to the old product;
    // the correct one (if any) comes back with the reconciling reload.
    var badge = line.querySelector('.cart-line__badge');
    if (badge) badge.hidden = true;
    var swapWrap = line.querySelector('.cart-line__swap');
    if (swapWrap) {
      swapWrap.hidden = true;
      // Mark the pill as spent so restoreState's one-pill pass doesn't re-show it
      // while the commit is in flight; the reconciling reload renders fresh DOM
      // without this marker.
      swapWrap.setAttribute('data-swap-consumed', '');
    }
  }

  function reload(cart) {
    if (cart) {
      document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true }));
    }
    document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
  }

  // Atomic update.js payload: add `wanted` (variant→qty) and drop `gone` (variant→0) in
  // ONE request, so the cart never passes through a two-line state (the source of the
  // "same product shown twice" flash) and only one round-trip is needed.
  function swapUpdates(wantedVariant, quantity, goneVariant) {
    var updates = {};
    updates[wantedVariant] = quantity;
    updates[goneVariant] = 0;
    return updates;
  }

  // Replace the original line with the swap product's variant.
  function doSwap(btn) {
    var originalVariant = btn.dataset.originalVariant;
    var swapVariant = btn.dataset.swapVariant;
    var quantity = parseInt(btn.dataset.originalQuantity, 10) || 1;
    var base = cartBase();

    // Snapshot the original line's exact markup before it's repainted, so undo can
    // drop it back in instantly without re-rendering or hitting the network.
    var originalLine = btn.closest('[data-cart-item]');
    if (originalLine) swapDom[swapVariant] = originalLine.cloneNode(true);

    // Record the swap and expose the undo affordance NOW, before the network — the undo
    // control must be clickable the instant the swap paints, not after the round-trip.
    var store = getStore();
    store[swapVariant] = { original: originalVariant, swap: swapVariant, quantity: quantity };
    setStore(store);
    if (originalLine) {
      var undoWrap = originalLine.querySelector('[data-cart-undo]');
      if (undoWrap) {
        undoWrap.dataset.variant = swapVariant; // re-key from the original to the swap variant
        undoWrap.removeAttribute('hidden');
      }
    }

    var state = { superseded: false };
    pending[swapVariant] = state;
    inflight++;
    btn.disabled = true;
    // Repaint to the swapped product instantly (no loader — same silent, optimistic
    // pattern as undo); the request below syncs in the background and reload reconciles.
    applyOptimisticSwap(originalLine);

    // One atomic update: add the swap variant and remove the original together, so the
    // cart goes original → swap in a single step (no intermediate both-lines render). A
    // failed update is a no-op, leaving the original untouched.
    var commit = postJSON(base + '/update.js', { updates: swapUpdates(swapVariant, quantity, originalVariant) });

    commit
      .then(function (cart) {
        // If an undo came in while this was in flight, let the undo's reload be the single
        // reconcile — re-rendering to the swapped line here would flash it back in.
        if (!state.superseded) reload(cart);
      })
      .catch(function (err) {
        var s = getStore();
        delete s[swapVariant];
        setStore(s);
        delete swapDom[swapVariant];
        btn.disabled = false;
        if (!state.superseded) {
          showError(friendlyError(err));
          reload();
        }
        console.error('[cart-swap] swap failed', err);
      })
      .finally(function () {
        inflight--;
        if (!state.superseded) delete pending[swapVariant];
      });

    state.promise = commit;
  }

  // Revert a swapped line back to the original product.
  function doUndo(wrapper) {
    var swapVariant = wrapper.dataset.variant;
    var record = getStore()[swapVariant];
    if (!record) return;
    var base = cartBase();

    // Guard against a second undo on the same line before it's removed (the fallback
    // path keeps the line in place until reload).
    var undoBtn = wrapper.querySelector('[data-cart-undo-btn]');
    if (undoBtn) {
      if (undoBtn.disabled) return;
      undoBtn.disabled = true;
    }

    // If the swap is still committing, this undo owns the reconcile — stop the commit
    // from re-rendering the swapped line back in once it lands.
    var commitState = pending[swapVariant];
    if (commitState) commitState.superseded = true;

    // Undoing dismisses this line's offer for good — the pill must not come back
    // on the restored line (or on any later re-render this session).
    addDismissed(record.original);

    /* Optimistic restore: put the original line back in place of the swapped line right
       away, so undo feels instant with no loading state. The requests below sync the
       server in the background, and the follow-up reload reconciles the authoritative
       line state + totals. If there's no in-memory snapshot (e.g. the page reloaded
       since the swap), fall back to the in-flight loading indicator. */
    var clone = swapDom[swapVariant];
    var swapLine = wrapper.closest('[data-cart-item]');
    if (clone && swapLine && swapLine.parentNode) {
      // The snapshot was taken while the pill was visible — hide it up front so
      // the dismissed offer never flashes back in (restoreState keeps it hidden).
      var clonePill = clone.querySelector('.cart-line__swap');
      if (clonePill) clonePill.hidden = true;
      swapLine.parentNode.insertBefore(clone, swapLine);
      swapLine.remove();
    } else {
      showLoadbar(wrapper);
    }

    inflight++;
    // Reverse on the network with one atomic update (re-add original, drop the swap line
    // together — no intermediate both-lines state). If the swap is still committing,
    // sequence behind it so the two writes don't race.
    var reverse = function () {
      var latest = getStore()[swapVariant];
      if (!latest) { reload(); return; } // swap already rolled itself back — just reconcile
      return postJSON(base + '/update.js', { updates: swapUpdates(latest.original, latest.quantity, swapVariant) })
        .then(function (cart) {
          var s = getStore();
          delete s[swapVariant];
          setStore(s);
          delete swapDom[swapVariant];
          reload(cart);
        })
        .catch(function (err) {
          showError(friendlyError(err));
          reload(); // re-fetch authoritative state — this rolls the optimistic restore back
          console.error('[cart-swap] undo failed', err);
        });
    };

    Promise.resolve(commitState && commitState.promise)
      .catch(function () {}) // a failed swap commit still lets undo reconcile to truth
      .then(reverse)
      .finally(function () {
        inflight--;
        delete pending[swapVariant];
      });
  }

  // Show the undo control on any line whose variant matches a stored swap. The
  // line's own swap pill is left as Liquid rendered it, so a swapped-in product
  // that has its OWN swap keeps offering it (swap chaining) alongside undo; a
  // swapped-in product with no swap renders no pill, leaving just undo. Runs
  // after every cart re-render.
  //
  // Also enforces the pill rules: a line whose swap was undone never shows its
  // pill again (dismissed), and of the remaining eligible pills only the FIRST
  // in DOM order is visible — swapping or dismissing one line reveals the next.
  function restoreState() {
    var store = getStore();
    document.querySelectorAll('[data-cart-undo]').forEach(function (wrapper) {
      if (store[wrapper.dataset.variant]) {
        wrapper.removeAttribute('hidden');
      } else {
        wrapper.setAttribute('hidden', '');
      }
    });

    var dismissed = getDismissed();
    var shown = false;
    document.querySelectorAll('.cart-line__swap').forEach(function (wrap) {
      // Spent by an in-flight optimistic swap — leave hidden until the reload reconciles.
      if (wrap.hasAttribute('data-swap-consumed')) return;
      var btn = wrap.querySelector('[data-cart-swap]');
      if (!btn || dismissed[btn.dataset.originalVariant] || shown) {
        wrap.hidden = true;
      } else {
        wrap.hidden = false;
        shown = true;
      }
    });
  }

  /* Forget dismissals whose product has left the cart, so re-adding the product
     offers its swap fresh. Runs on every cart write — theme:cart:change carries
     the authoritative cart from all paths (quantity, trash, variant change,
     offers, our own swap/undo). A variant that is the `original` of an active
     swap record is kept even while absent: mid-undo the cart lacks it only
     until the undo's own write restores it, and a change event from another
     operation landing in that window must not wipe the dismissal. */
  document.addEventListener('theme:cart:change', function (e) {
    var cart = e.detail && e.detail.cart;
    if (!cart || !cart.items) return;
    var dismissed = getDismissed();
    var keys = Object.keys(dismissed);
    if (!keys.length) return;
    var present = {};
    cart.items.forEach(function (item) {
      present[item.variant_id] = true;
    });
    var store = getStore();
    var undoing = {};
    Object.keys(store).forEach(function (k) {
      undoing[store[k].original] = true;
    });
    var changed = false;
    keys.forEach(function (variantId) {
      if (!present[variantId] && !undoing[variantId]) {
        delete dismissed[variantId];
        changed = true;
      }
    });
    if (changed) setDismissed(dismissed);
  });

  document.addEventListener('click', function (e) {
    var swapBtn = e.target.closest('[data-cart-swap]');
    if (swapBtn) {
      if (inflight) return; // debounce: don't start a swap while another op is in flight
      doSwap(swapBtn);
      return;
    }
    // Undo is allowed even while a swap is still committing — that's what lets it feel instant.
    var undoBtn = e.target.closest('[data-cart-undo-btn]');
    if (undoBtn) {
      var wrapper = undoBtn.closest('[data-cart-undo]');
      if (wrapper) doUndo(wrapper);
    }
  });

  // Re-apply undo/swap visibility whenever the line items are re-rendered.
  function observe() {
    var container = document.querySelector(ITEMS);
    if (container) {
      new MutationObserver(restoreState).observe(container, { childList: true, subtree: true });
    }
    restoreState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }
})();
